import { createRoute, createRouter, redirect } from "@tanstack/react-router";
import { rootRoute } from "./routes/root-route";
import { LoginPage } from "./routes/login";
import { ForgotPasswordPage } from "./routes/forgot-password";
import { ResetPasswordPage } from "./routes/reset-password";
import { NotFoundPage } from "./routes/not-found";
import { CatalogueLayout } from "./areas/office/catalogue/catalogue-layout";
import { ProductsListScreen } from "./areas/office/catalogue/screens/products-list";
import { ProductDetailScreen } from "./areas/office/catalogue/screens/product-detail";
import { BomEditorScreen } from "./areas/office/catalogue/screens/bom-editor";
import { RoutingEditorScreen } from "./areas/office/catalogue/screens/routing-editor";
import { UomSettingsScreen } from "./areas/office/catalogue/screens/uom-settings";
import { CostRunsScreen } from "./areas/office/catalogue/screens/cost-runs";
import { CostRunDetailScreen } from "./areas/office/catalogue/screens/cost-run-detail";
import { TemplatesScreen } from "./areas/office/catalogue/screens/templates";
import { ImportsScreen } from "./areas/office/catalogue/screens/imports";
import { InventoryLayout } from "./areas/office/inventory/inventory-layout";
import { StockLookupScreen } from "./areas/office/inventory/screens/stock-lookup";
import { StockUnitCardScreen } from "./areas/office/inventory/screens/stock-unit-card";
import { LocationsBrowserScreen } from "./areas/office/inventory/screens/locations-browser";
import { TransfersListScreen } from "./areas/office/inventory/screens/transfers-list";
import { TransferDetailScreen } from "./areas/office/inventory/screens/transfer-detail";
import { CountsListScreen } from "./areas/office/inventory/screens/counts-list";
import { CountReviewScreen } from "./areas/office/inventory/screens/count-review";
import { LotDetailScreen } from "./areas/office/inventory/screens/lot-detail";
import { TraceViewerScreen } from "./areas/office/inventory/screens/trace-viewer";
import { MovementsLedgerScreen } from "./areas/office/inventory/screens/movements-ledger";
import { AtpInspectorScreen } from "./areas/office/inventory/screens/atp-inspector";
import { UomConversionsScreen } from "./areas/office/inventory/screens/uom-conversions";
import { AdjustmentApprovalsScreen } from "./areas/office/inventory/screens/approvals";
import { StockAdjustScreen } from "./areas/office/inventory/screens/stock-adjust";
import { AdjustmentRequestScreen } from "./areas/office/inventory/screens/adjustment-request";
import { WasteSettingsScreen } from "./areas/office/waste/screens/settings";
import { WasteIndexRedirect, WasteLayout } from "./areas/office/waste/waste-layout";
import { validateWasteEventsSearch, WasteEventsScreen } from "./areas/office/waste/screens/events";
import { validateWasteShiftsSearch, WasteShiftsScreen } from "./areas/office/waste/screens/shifts";
import { validateWasteParetoSearch, WasteParetoScreen } from "./areas/office/waste/screens/pareto";
import { WasteRemnantsScreen } from "./areas/office/waste/screens/remnants";
import { PlanningLayout } from "./areas/office/planning/planning-layout";
import { PlanningCalendarsScreen } from "./areas/office/planning/screens/calendars";
import { PlanningCapacityScreen } from "./areas/office/planning/screens/capacity-load";
import { PlanningScheduleScreen } from "./areas/office/planning/screens/schedule-board";
import { PlanningSetupMatrixScreen } from "./areas/office/planning/screens/setup-matrix";
import { PlanningDemandScreen } from "./areas/office/planning/screens/demand";
import { PlanningForecastsScreen } from "./areas/office/planning/screens/forecasts";
import { PlanningForecastEditorScreen } from "./areas/office/planning/screens/forecast-editor";
import { PlanningMpsScreen } from "./areas/office/planning/screens/mps";
import { PlanningMrpRunScreen } from "./areas/office/planning/screens/mrp-run";
import { PlanningPlannedOrdersScreen } from "./areas/office/planning/screens/planned-orders";
import { PlanningPromisesScreen } from "./areas/office/planning/screens/promises";
import { PlanningScenariosScreen } from "./areas/office/planning/screens/scenarios";
import { PlanningScenarioBuilderScreen } from "./areas/office/planning/screens/scenario-builder";
import { PlanningScenarioResultScreen } from "./areas/office/planning/screens/scenario-result";
import {
  validateScenarioResultSearch,
  validateScenariosSearch,
} from "./areas/office/planning/lib/scenario-lib";
import { validatePromisesSearch } from "./areas/office/planning/lib/promise-lib";
import { validatePlannedOrdersSearch } from "./areas/office/planning/lib/mrp-lib";
import {
  validateCapacitySearch,
  validateScheduleSearch,
  validateSetupMatrixSearch,
} from "./areas/office/planning/lib/schedule-lib";
import { PlanningParametersScreen } from "./areas/office/planning/screens/parameters";
import { PlanningSettingsScreen } from "./areas/office/planning/screens/settings";
import { PlanningWorkbenchScreen } from "./areas/office/planning/screens/workbench";
import { AgentsLayout } from "./areas/office/agents/agents-layout";
import { AgentsHubScreen, validateHubSearch } from "./areas/office/agents/screens/hub";
import { AgentsJournalScreen } from "./areas/office/agents/screens/journal";
import { AgentDetailScreen } from "./areas/office/agents/screens/agent-detail";
import { validateJournalSearch } from "./areas/office/agents/lib/agents-lib";
import { FloorRemnantScreen, FloorWasteCaptureScreen } from "./areas/floor-pwa/screens/floor-waste";
import {
  FloorQualityQueueScreen,
  FloorQualityRunScreen,
} from "./areas/floor-pwa/screens/floor-quality";
import { FloorScoreboardScreen } from "./areas/floor-pwa/screens/floor-scoreboard";
import { FloorGuidedCutScreen } from "./areas/floor-pwa/screens/floor-cut";
import { QualityIndexRedirect, QualityLayout } from "./areas/office/quality/quality-layout";
import { SalesIndexRedirect, SalesLayout } from "./areas/office/sales/sales-layout";
import { MessagingIndexRedirect, MessagingLayout } from "./areas/office/messaging/messaging-layout";
import { MessagingInboxScreen, validateInboxSearch } from "./areas/office/messaging/screens/inbox";
import { MessagingTemplatesScreen } from "./areas/office/messaging/screens/templates";
import { MessagingSettingsScreen } from "./areas/office/messaging/screens/settings";
import { SalesCustomersScreen } from "./areas/office/sales/screens/customers";
import { SalesQuotesScreen } from "./areas/office/sales/screens/quotes";
import { SalesQuoteComposerScreen } from "./areas/office/sales/screens/quote-composer";
import { SalesReviewsScreen } from "./areas/office/sales/screens/reviews";
import { SalesOrdersScreen } from "./areas/office/sales/screens/orders";
import { SalesOrderDetailScreen } from "./areas/office/sales/screens/order-detail";
import {
  SalesOrderImportJobScreen,
  SalesOrdersImportScreen,
} from "./areas/office/sales/screens/orders-import";
import {
  validateSalesComposerSearch,
  validateSalesCustomersSearch,
  validateSalesOrdersSearch,
  validateSalesQuotesSearch,
} from "./areas/office/sales/screens/search-params";
import { SalesCustomer360Screen } from "./areas/office/sales/screens/customer-360";
import { SalesCustomerMergeScreen } from "./areas/office/sales/screens/customer-merge";
import {
  SalesCustomerImportJobScreen,
  SalesCustomersImportScreen,
} from "./areas/office/sales/screens/customers-import";
import { SalesPriceListsScreen } from "./areas/office/sales/screens/price-lists";
import { SalesPriceListDetailScreen } from "./areas/office/sales/screens/price-list-detail";
import { SalesOffcutClassesScreen } from "./areas/office/sales/screens/offcut-classes";
import { SalesMarginGuardsScreen } from "./areas/office/sales/screens/margin-guards";
import { SalesTasksScreen } from "./areas/office/sales/screens/tasks";
import { validateSalesTasksSearch } from "./areas/office/sales/screens/tasks-search-params";
import { SalesAnalyticsScreen } from "./areas/office/sales/screens/analytics";
import { SalesSettingsScreen } from "./areas/office/sales/screens/settings";
import { SalesPipelineBoardScreen } from "./areas/office/sales/screens/pipeline-board";
import { SalesOpportunitiesScreen } from "./areas/office/sales/screens/opportunities";
import { SalesOpportunityDetailScreen } from "./areas/office/sales/screens/opportunity-detail";
import { SalesForecastScreen } from "./areas/office/sales/screens/forecast";
import {
  validateSalesForecastSearch,
  validateSalesOpportunitiesSearch,
  validateSalesPipelineSearch,
} from "./areas/office/sales/screens/pipeline-search-params";
import {
  ControlPointsScreen,
  validateControlPointsSearch,
} from "./areas/office/quality/screens/control-points";
import {
  ControlPointEditorScreen,
  validateControlPointNewSearch,
} from "./areas/office/quality/screens/control-point-editor";
import {
  FailureCausesScreen,
  validateFailureCausesSearch,
} from "./areas/office/quality/screens/failure-causes";
import {
  InspectionsScreen,
  validateInspectionsSearch,
} from "./areas/office/quality/screens/inspections";
import { InspectionDetailScreen } from "./areas/office/quality/screens/inspection-detail";
import { CoaManagerScreen } from "./areas/office/quality/screens/coa-manager";
import { QualityAnalyticsScreen } from "./areas/office/quality/screens/analytics";
import {
  QuarantineBoardScreen,
  validateQuarantineSearch,
} from "./areas/office/quality/screens/quarantine-board";
import { AuditTrailScreen } from "./areas/office/audit/screens/audit-trail";
import { validateAuditSearch } from "./areas/office/audit/screens/search-params";
import {
  validateAdjustSearch,
  validateLookupSearch,
  validateMovementsSearch,
  validateTransfersSearch,
} from "./areas/office/inventory/screens/search-params";
import { ProductionLayout } from "./areas/office/production/production-layout";
import { WorkOrdersListScreen } from "./areas/office/production/screens/work-orders-list";
import { WorkOrderDetailScreen } from "./areas/office/production/screens/work-order-detail";
import { DispatchBoardScreen } from "./areas/office/production/screens/dispatch-board";
import { ConflictsScreen } from "./areas/office/production/screens/conflicts";
import { DevicesScreen } from "./areas/office/production/screens/devices";
import { CalibrationScreen } from "./areas/office/production/screens/calibration";
import { ShiftsScreen } from "./areas/office/production/screens/shifts";
import { BlockedStationsScreen } from "./areas/office/production/screens/blocked-stations";
import { PieceRateScreen } from "./areas/office/production/screens/piece-rate";
import {
  validateBlockedSearch,
  validateDispatchSearch,
  validatePieceRateSearch,
  validateWorkOrdersSearch,
} from "./areas/office/production/screens/search-params";
import { FloorLayout } from "./areas/floor-pwa/floor-layout";
import { FloorLookupScreen } from "./areas/floor-pwa/screens/lookup";
import { FloorHomeScreen } from "./areas/floor-pwa/screens/floor-home";
import { FloorScanScreen } from "./areas/floor-pwa/screens/floor-scan";
import { FloorStockScreen } from "./areas/floor-pwa/screens/floor-stock";
import { FloorUnitScreen } from "./areas/floor-pwa/screens/floor-unit";
import { FloorCountsScreen } from "./areas/floor-pwa/screens/floor-counts";
import { FloorCountWorksheetScreen } from "./areas/floor-pwa/screens/floor-count";
import {
  FloorReceiveListScreen,
  FloorReceiveScreen,
} from "./areas/floor-pwa/screens/floor-receive";
import { FloorTrayScreen } from "./areas/floor-pwa/screens/floor-tray";
import { FloorShipQueueScreen } from "./areas/floor-pwa/screens/floor-ship-queue";
import { FloorShipScreen } from "./areas/floor-pwa/screens/floor-ship";
import { StationHomeScreen, StationPickerScreen } from "./areas/floor-pwa/screens/station-home";
import { OpRunScreen } from "./areas/floor-pwa/screens/op-run";
import { QrOpResolverScreen } from "./areas/floor-pwa/screens/qr-resolve";
import { pinStation } from "./areas/floor-pwa/lib/production-floor";
import { MyShiftScreen } from "./areas/floor-pwa/screens/my-shift";
import { WallBoardScreen } from "./areas/floor-pwa/screens/wall-board";
import { HomeScreen } from "./areas/office/home/home-screen";
import { OnboardingLayout } from "./areas/onboarding/onboarding-layout";
import { WizardHomeScreen } from "./areas/onboarding/screens/wizard-home";
import { VerticalPickerScreen } from "./areas/onboarding/screens/vertical-picker";
import { CatalogueStepScreen } from "./areas/onboarding/screens/catalogue-step";
import { ImportDetailScreen } from "./areas/onboarding/screens/import-detail";
import { PlantMapScreen } from "./areas/onboarding/screens/plant-map";
import { OpeningStepScreen } from "./areas/onboarding/screens/opening-step";
import { QualityReviewScreen } from "./areas/onboarding/screens/quality-review";
import { ChecklistScreen } from "./areas/onboarding/screens/checklist";
import { TerminologySettingsScreen } from "./areas/onboarding/screens/terminology-settings";
import { IndustryPackScreen } from "./areas/onboarding/screens/industry-pack";
import { consoleRouteTrees } from "./areas/admin/console-routes";
import { ImpersonationLanding } from "./routes/impersonate";
import { PublicQuoteScreen } from "./areas/public/screens/quote-accept";
import { PublicOrderTrackScreen } from "./areas/public/screens/order-track";
import { isAdminHost } from "./lib/host";

// The root route lives in routes/root-route.ts, so an area route table (the
// console's) can hang from it without importing this file.

// Unauthenticated entry: the walkthrough starts at the tenant root and signs
// in. Index → /login; a successful login navigates to /home (login.tsx
// `landingRoute()` — the operations overview, or the role/tenant-specific
// landing it names).
//
// ON THE ADMIN HOST IT AIMS AT `/console` INSTEAD, and the guard decides. A
// bare redirect to /login showed the sign-in form to somebody who was already
// signed in — a bookmarked console root, the commonest way anyone returns to
// this host, answered "sign in" to a live session. `beforeLoad` is synchronous
// and cannot probe `GET /v1/platform/me`, so this does not try to: it sends the
// browser to the screen that ASKS, and RequirePlatformSession bounces a 401
// straight back to /login (and a totp_pending session to /enroll) without ever
// painting console chrome. Signed in → the console; signed out → the same
// /login as before, one hop later.
const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  beforeLoad: () => {
    const to = isAdminHost(window.location.hostname) ? "/console" : "/login";
    // redirect() is TanStack Router's control-flow signal — thrown, not an
    // Error subclass, so the only-throw-error rule can't recognize it.
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw redirect({ to });
  },
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: LoginPage,
});

// The password-reset pair (UX-6a). `/reset` is the EMAIL'S path — the API's
// reset mail links `${appBaseUrl}/reset?token=…` (identity/reset.service.ts),
// so the route answers that address exactly; `/forgot-password` is the
// screen-side entry the login card links to. Both render on the same
// chromeless auth canvas as /login (routes/root.tsx).
const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/forgot-password",
  component: ForgotPasswordPage,
});

const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/reset",
  component: ResetPasswordPage,
});

// ---- Office home (UX-0, design-system spec §6) ------------------------------
// The operations overview — reference screen 01, the sidebar's Home entry, and
// THE POST-LOGIN LANDING for every office role (login.tsx `landingRoute()`).
const homeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/home",
  component: HomeScreen,
});

// ---- The RETIRED P0 walking-skeleton products slice -------------------------
// `/products` and `/products/new` were the P0 skeleton's own list + create form,
// kept alive after Module 01 shipped because the login flow landed there and the
// certified walkthrough drove them. That made the FIRST screen after sign-in a
// flat table with no row links — a dead end that could not reach a product, next
// to a nav that also offered the real Module 01 list at `/catalogue/products`.
// Two owner reports, one cause; the owner ruled the legacy pages retired, the
// landing moved to `/home`, and the walkthrough retargeted at the surface that
// ships (see e2e/specs/p0-skeleton/W-SKELETON.walking-skeleton.spec.ts).
//
// THE PATHS STAY, AS REDIRECTS ONLY: they were the landing for months, so they
// are in browser history, bookmarks and pinned tabs. Deleting the routes would
// answer those with the designed 404; these answer with the screen that replaced
// them. `/products/new` lands on the list because the create flow is that
// screen's own template-picker panel, not a route of its own.
const productsRedirectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/products",
  beforeLoad: () => {
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw redirect({ to: "/catalogue/products" });
  },
});

const newProductRedirectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/products/new",
  beforeLoad: () => {
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw redirect({ to: "/catalogue/products" });
  },
});

// ---- Office · Catalogue (Module 01, S1–S8) ---------------------------------
// The catalogue area shell + child routes. Each screen is a SCREEN-STUB the
// Stage-2 builders replace in place (screens/*.tsx), keeping the route wiring.

const catalogueRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/catalogue",
  component: CatalogueLayout,
});

const catalogueIndexRoute = createRoute({
  getParentRoute: () => catalogueRoute,
  path: "/",
  beforeLoad: () => {
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw redirect({ to: "/catalogue/products" });
  },
});

const catalogueProductsRoute = createRoute({
  getParentRoute: () => catalogueRoute,
  path: "products",
  // UX-0: the topbar's global search lands here carrying ?q= — the list screen
  // seeds its search filter from it (design-system spec §3.1 topbar).
  validateSearch: (search: Record<string, unknown>): { q?: string } =>
    typeof search["q"] === "string" && search["q"].length > 0 ? { q: search["q"] } : {},
  component: ProductsListScreen,
});
const catalogueProductDetailRoute = createRoute({
  getParentRoute: () => catalogueRoute,
  path: "products/$id",
  component: ProductDetailScreen,
});
const catalogueBomEditorRoute = createRoute({
  getParentRoute: () => catalogueRoute,
  path: "products/$id/boms/$bomId",
  component: BomEditorScreen,
});
const catalogueRoutingEditorRoute = createRoute({
  getParentRoute: () => catalogueRoute,
  path: "products/$id/routings/$routingId",
  component: RoutingEditorScreen,
});
const catalogueUomsRoute = createRoute({
  getParentRoute: () => catalogueRoute,
  path: "uoms",
  component: UomSettingsScreen,
});
const catalogueCostRunsRoute = createRoute({
  getParentRoute: () => catalogueRoute,
  path: "cost-runs",
  component: CostRunsScreen,
});
const catalogueCostRunDetailRoute = createRoute({
  getParentRoute: () => catalogueRoute,
  path: "cost-runs/$id",
  component: CostRunDetailScreen,
});
const catalogueTemplatesRoute = createRoute({
  getParentRoute: () => catalogueRoute,
  path: "templates",
  component: TemplatesScreen,
});
const catalogueImportsRoute = createRoute({
  getParentRoute: () => catalogueRoute,
  path: "imports",
  component: ImportsScreen,
});

const catalogueRouteTree = catalogueRoute.addChildren([
  catalogueIndexRoute,
  catalogueProductsRoute,
  catalogueProductDetailRoute,
  catalogueBomEditorRoute,
  catalogueRoutingEditorRoute,
  catalogueUomsRoute,
  catalogueCostRunsRoute,
  catalogueCostRunDetailRoute,
  catalogueTemplatesRoute,
  catalogueImportsRoute,
]);

// ---- Office · Inventory (Module 06, §4.1–§4.10) ----------------------------
// The office half of module 06. Mirrors the catalogue block: one area shell
// (/inventory) with an index redirect onto the flagship §4.1 lookup, which §4.1
// names the storekeeper's default landing screen.
//
// DELIBERATE SHAPE NOTES (each recorded in the spec §4 rows amended in this PR):
//   * The FLOOR screens (§4.1 floor variant, §4.2 floor action bar, §4.4
//     Transfer Receive, §4.5 Count Worksheet) and the offline outbox are the
//     NEXT slice (D-048). Nothing here pretends to be offline-capable.
//   * §4.5's review screen works on one count, so /inventory/counts is its
//     inbox: the counts in `review`/`pending_approval` a reviewer must act on.
//   * §4.7's trace viewer is rooted on a LOT because GET /lots/{id}/trace is the
//     only trace surface (§5 row 11) — hence /inventory/lots/$id/trace rather
//     than a free-standing /trace with an ad-hoc id box.
//   * Three routes declare `validateSearch` so §4.9's drill links carry their
//     filter through the navigation (see screens/search-params.ts).

const inventoryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/inventory",
  component: InventoryLayout,
});

const inventoryIndexRoute = createRoute({
  getParentRoute: () => inventoryRoute,
  path: "/",
  beforeLoad: () => {
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw redirect({ to: "/inventory/lookup" });
  },
});

const inventoryLookupRoute = createRoute({
  getParentRoute: () => inventoryRoute,
  path: "lookup",
  validateSearch: validateLookupSearch,
  component: StockLookupScreen,
});
const inventoryUnitRoute = createRoute({
  getParentRoute: () => inventoryRoute,
  path: "units/$id",
  component: StockUnitCardScreen,
});
const inventoryLocationsRoute = createRoute({
  getParentRoute: () => inventoryRoute,
  path: "locations",
  component: LocationsBrowserScreen,
});
const inventoryTransfersRoute = createRoute({
  getParentRoute: () => inventoryRoute,
  path: "transfers",
  validateSearch: validateTransfersSearch,
  component: TransfersListScreen,
});
const inventoryTransferDetailRoute = createRoute({
  getParentRoute: () => inventoryRoute,
  path: "transfers/$id",
  component: TransferDetailScreen,
});
const inventoryCountsRoute = createRoute({
  getParentRoute: () => inventoryRoute,
  path: "counts",
  component: CountsListScreen,
});
const inventoryCountReviewRoute = createRoute({
  getParentRoute: () => inventoryRoute,
  path: "counts/$id",
  component: CountReviewScreen,
});
const inventoryLotRoute = createRoute({
  getParentRoute: () => inventoryRoute,
  path: "lots/$id",
  component: LotDetailScreen,
});
const inventoryLotTraceRoute = createRoute({
  getParentRoute: () => inventoryRoute,
  path: "lots/$id/trace",
  component: TraceViewerScreen,
});
const inventoryMovementsRoute = createRoute({
  getParentRoute: () => inventoryRoute,
  path: "movements",
  validateSearch: validateMovementsSearch,
  component: MovementsLedgerScreen,
});
const inventoryAtpRoute = createRoute({
  getParentRoute: () => inventoryRoute,
  path: "atp",
  component: AtpInspectorScreen,
});
const inventoryUomRoute = createRoute({
  getParentRoute: () => inventoryRoute,
  path: "uom-conversions",
  component: UomConversionsScreen,
});
// §4.11, added in the phase-3a fix round: the §3.9 approval hold had no screen
// at all, so a 202'd adjustment and an escalated count both dead-ended.
const inventoryApprovalsRoute = createRoute({
  getParentRoute: () => inventoryRoute,
  path: "approvals",
  component: AdjustmentApprovalsScreen,
});

// §4.12 (D-315): the office stock adjustment, and the pending document a
// 202 links to. `approvals/$id` is a sibling of `approvals`, not a child, so
// the request page does not render inside the list.
const inventoryAdjustRoute = createRoute({
  getParentRoute: () => inventoryRoute,
  path: "adjust",
  validateSearch: validateAdjustSearch,
  component: StockAdjustScreen,
});
const inventoryApprovalRequestRoute = createRoute({
  getParentRoute: () => inventoryRoute,
  path: "approvals/$id",
  component: AdjustmentRequestScreen,
});

const inventoryRouteTree = inventoryRoute.addChildren([
  inventoryIndexRoute,
  inventoryLookupRoute,
  inventoryUnitRoute,
  inventoryLocationsRoute,
  inventoryTransfersRoute,
  inventoryTransferDetailRoute,
  inventoryCountsRoute,
  inventoryCountReviewRoute,
  inventoryLotRoute,
  inventoryLotTraceRoute,
  inventoryMovementsRoute,
  inventoryAtpRoute,
  inventoryUomRoute,
  inventoryApprovalsRoute,
  inventoryAdjustRoute,
  inventoryApprovalRequestRoute,
]);

// ---- Office · Onboarding wizard (Module 02, S1–S9) -------------------------
// The wizard shell (S1) owns the step rail, the completeness dial and the
// session state; each child route is a step screen or a wizard-adjacent screen.
// Screens are SCREEN-STUBs the S2–S9 builders replace in place, keeping the
// route wiring.
//
// DELIBERATE GAP — ONE screen in spec 02 §4 has no route here, because it has
// no API to call and a route to a screen that cannot work is a dead end:
//   * S6 QR print kit (`/onboarding/qr-kit`).
//     STALE REASON CORRECTED 2026-08-02: this used to say the API and the PDF
//     renderer were both unbuilt and "verified absent from openapi/v1.json".
//     They are BUILT (D-055) — all three `/v1/qr-kits` paths are in
//     openapi/v1.json today and spec 02 §4 S6 records the API as shipped.
//     The CURRENT reason the wizard screen has no route is a product decision,
//     not a missing dependency: TWO DOORS ALREADY EXIST — the REST/agent
//     surface, and module 06 §4.3's locations browser ("Print QR labels"),
//     which is the storekeeper's real workflow because they are standing at
//     the rack. A second selector in the wizard before that one has been used
//     would be a screen invented ahead of the need; it lands with the S6 slice
//     and needs no server change.
//
// S7 (step 4 · Opening state) IS routed even though Modules 06/09/10 are
// unwired. Leaving it out was not a neutral omission: step 4 is one of the four
// completions `ready_for_review` requires (§3.1), so with no screen the session
// could never leave `in_progress`, go-live answered 422 PRECONDITIONS_UNMET
// forever, and the wizard had no terminal outcome at all. The screen ships with
// the five opening import lanes that are genuinely built and is explicit about
// what is deferred.

const onboardingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/onboarding",
  component: OnboardingLayout,
});

const onboardingIndexRoute = createRoute({
  getParentRoute: () => onboardingRoute,
  path: "/",
  component: WizardHomeScreen,
});

const onboardingVerticalRoute = createRoute({
  getParentRoute: () => onboardingRoute,
  path: "vertical",
  component: VerticalPickerScreen,
});
const onboardingCatalogueRoute = createRoute({
  getParentRoute: () => onboardingRoute,
  path: "catalogue",
  component: CatalogueStepScreen,
});
const onboardingImportDetailRoute = createRoute({
  getParentRoute: () => onboardingRoute,
  path: "imports/$id",
  component: ImportDetailScreen,
});
const onboardingPlantRoute = createRoute({
  getParentRoute: () => onboardingRoute,
  path: "plant",
  component: PlantMapScreen,
});
const onboardingOpeningRoute = createRoute({
  getParentRoute: () => onboardingRoute,
  path: "opening",
  component: OpeningStepScreen,
});
const onboardingChecklistRoute = createRoute({
  getParentRoute: () => onboardingRoute,
  path: "checklist",
  component: ChecklistScreen,
});
const onboardingQualityRoute = createRoute({
  getParentRoute: () => onboardingRoute,
  path: "quality",
  component: QualityReviewScreen,
});

const onboardingRouteTree = onboardingRoute.addChildren([
  onboardingIndexRoute,
  onboardingVerticalRoute,
  onboardingCatalogueRoute,
  onboardingImportDetailRoute,
  onboardingPlantRoute,
  onboardingOpeningRoute,
  onboardingQualityRoute,
  onboardingChecklistRoute,
]);

// ---- Module 05 waste (slices 05-A + 05-B) ----------------------------------
//
// 05-A shipped one screen (§4 S11's settings/catalog editor); 05-B adds the
// area shell and three data screens — S2 (/waste/events, the ledger), S4
// (/waste/shifts, the shift scoreboard incl. ?kiosk=1 wall mode) and S5
// (/waste/remnants, the registry). The other §4 screens (S1 command board, S3
// variance, S6 matches, S7 PVC, S10 sales) have no data behind them yet and
// get NO route in any form (D-033; spec 05 §4 slice ownership). The index
// redirects to the ledger — the screen that answers without an id in hand.
const wasteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/waste",
  component: WasteLayout,
});
const wasteIndexRoute = createRoute({
  getParentRoute: () => wasteRoute,
  path: "/",
  // NOT a `beforeLoad` redirect: it cannot read the role feed, so it sent every
  // caller to the ledger — including the storekeeper and sales roles that hold
  // `waste.remnant.manage` and no `waste.read` at all. See `WasteIndexRedirect`.
  component: WasteIndexRedirect,
});
const wasteEventsRoute = createRoute({
  getParentRoute: () => wasteRoute,
  path: "events",
  validateSearch: validateWasteEventsSearch,
  component: WasteEventsScreen,
});
const wasteShiftsRoute = createRoute({
  getParentRoute: () => wasteRoute,
  path: "shifts",
  validateSearch: validateWasteShiftsSearch,
  component: WasteShiftsScreen,
});
// S13 (2026-08-20) — the scrap Pareto. `facility_id`, `from`, `to` and
// `group_by` survive validation so a month's ranking is a LINK: the number a GM
// argues about on Monday has to be sendable to the plant manager unchanged.
const wasteParetoRoute = createRoute({
  getParentRoute: () => wasteRoute,
  path: "pareto",
  validateSearch: validateWasteParetoSearch,
  component: WasteParetoScreen,
});
const wasteRemnantsRoute = createRoute({
  getParentRoute: () => wasteRoute,
  path: "remnants",
  component: WasteRemnantsScreen,
});
const wasteSettingsRoute = createRoute({
  getParentRoute: () => wasteRoute,
  path: "settings",
  component: WasteSettingsScreen,
});
const wasteRouteTree = wasteRoute.addChildren([
  wasteIndexRoute,
  wasteEventsRoute,
  wasteShiftsRoute,
  wasteParetoRoute,
  wasteRemnantsRoute,
  wasteSettingsRoute,
]);

// ---- Office · Production (Module 04 §4 — S1–S4 from 04-A; S7/S8 from 04-B) --
//
// 04-A shipped S1 (work order list), S2 (detail), S3 (a MODAL launched from
// S1/S2 — no route of its own) and S4 (the dispatch board); 04-B adds S7
// (/production/conflicts) and S8 (/production/devices), the office half of
// the floor spine. S5/S6/S11/S12 have no data behind them yet and get NO
// route in any form — no /production/shifts, no calibration (D-033; spec 04
// §4's slice-ownership paragraph). The index redirects to the list so a typed
// /production lands somewhere real.
//
// The screen components are SCREEN-STUBs the 04-A screens agent replaces in
// place (screens/*.tsx), keeping this route wiring — the catalogue/onboarding
// precedent. Each renders only the area loading state until then.

const productionRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/production",
  component: ProductionLayout,
});

const productionIndexRoute = createRoute({
  getParentRoute: () => productionRoute,
  path: "/",
  beforeLoad: () => {
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw redirect({ to: "/production/work-orders" });
  },
});

const productionWorkOrdersRoute = createRoute({
  getParentRoute: () => productionRoute,
  path: "work-orders",
  // §4 S1: the filters, the sort and `view` live in the URL. The three chips
  // that were URL presets over the same params are system saved views since
  // PR-E4, rows 0070 inserts and `/v1/saved-views` serves beside the
  // reader's own and shared views (D-305 ruling 11, spec 16 §3.12).
  validateSearch: validateWorkOrdersSearch,
  component: WorkOrdersListScreen,
});
const productionWorkOrderDetailRoute = createRoute({
  getParentRoute: () => productionRoute,
  path: "work-orders/$id",
  component: WorkOrderDetailScreen,
});
const productionDispatchRoute = createRoute({
  getParentRoute: () => productionRoute,
  path: "dispatch",
  validateSearch: validateDispatchSearch,
  component: DispatchBoardScreen,
});
// 04-B adds S7 + S8; 04-C-2 adds S6; 04-C-4 adds S5 (/production/shifts) and
// the two floor screens S11 + S12 (spec 04 §4 slice ownership). The D-033
// statement above still governs what is left: no route exists for a screen
// whose data does not.
const productionConflictsRoute = createRoute({
  getParentRoute: () => productionRoute,
  path: "conflicts",
  component: ConflictsScreen,
});
const productionDevicesRoute = createRoute({
  getParentRoute: () => productionRoute,
  path: "devices",
  component: DevicesScreen,
});
const productionCalibrationRoute = createRoute({
  getParentRoute: () => productionRoute,
  path: "calibration",
  component: CalibrationScreen,
});
const productionShiftsRoute = createRoute({
  getParentRoute: () => productionRoute,
  path: "shifts",
  component: ShiftsScreen,
});
// S13 — the blocked-stations board. §5 e22 has served open downtime since
// 04-C-1 with no office consumer at all; this is it. The facility narrowing
// lives in the URL (and is genuinely optional — see validateBlockedSearch).
const productionBlockedRoute = createRoute({
  getParentRoute: () => productionRoute,
  path: "blocked",
  validateSearch: validateBlockedSearch,
  component: BlockedStationsScreen,
});
// S14 — the piece-rate ledger. §5 e27 has served the §2.13 capture rows since
// 04-C-4 with no consumer at all (a hook with no call site), which is the same
// defect class as e22's above and answered the same way. The period filter lives
// in the URL: a payroll period re-picked after every reload is one somebody gets
// wrong once.
const productionPieceRateRoute = createRoute({
  getParentRoute: () => productionRoute,
  path: "piece-rate",
  validateSearch: validatePieceRateSearch,
  component: PieceRateScreen,
});

const productionRouteTree = productionRoute.addChildren([
  productionIndexRoute,
  productionWorkOrdersRoute,
  productionWorkOrderDetailRoute,
  productionDispatchRoute,
  productionConflictsRoute,
  productionDevicesRoute,
  productionCalibrationRoute,
  productionShiftsRoute,
  productionBlockedRoute,
  productionPieceRateRoute,
]);

// ---- Office · Quality (Module 07 §4 — slice 07-A) --------------------------
//
// 07-A ships the office half of the spine: S4.1 (the control-point library),
// S4.2 (its editor, which drives the §3.1 step-5 version flip the SERVER
// performs) and S4.3 (the inspection queue), plus S4.4's execution screen —
// which is ONE component (`run/inspection-run.tsx`) hosted here and on the
// floor, because §4.4 says the office fallback is identical.
//
// WHAT GETS NO ROUTE, IN ANY FORM (D-033, D-123 §0.2): the guided cut (§4.5)
// is 07-C's, the CoA manager (§4.7) and quality analytics (§4.8) 07-E's.
// Their endpoints are absent from openapi/v1.json entirely, so a route to any
// of them would be a screen that 404s on its first read — the disabled shell
// D-026 refuses in a costume. The Quarantine Board (§4.6) landed with 07-B-3,
// on the live 07-B-1/07-B-2 holds surface.
//
// The index redirects through a COMPONENT, not a `beforeLoad`: `beforeLoad`
// cannot read the role feed, and `finance`/`sales` hold `quality.inspection.read`
// and NOT `quality.catalog.read` — a static redirect would land them on the one
// screen in the area they are refused (the defect module 05 shipped and fixed).
const qualityRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/quality",
  component: QualityLayout,
});
const qualityIndexRoute = createRoute({
  getParentRoute: () => qualityRoute,
  path: "/",
  component: QualityIndexRedirect,
});
const qualityInspectionsRoute = createRoute({
  getParentRoute: () => qualityRoute,
  path: "inspections",
  validateSearch: validateInspectionsSearch,
  component: InspectionsScreen,
});
const qualityInspectionRoute = createRoute({
  getParentRoute: () => qualityRoute,
  path: "inspections/$id",
  component: InspectionDetailScreen,
});
const qualityQuarantineRoute = createRoute({
  getParentRoute: () => qualityRoute,
  path: "quarantine",
  validateSearch: validateQuarantineSearch,
  component: QuarantineBoardScreen,
});
const qualityControlPointsRoute = createRoute({
  getParentRoute: () => qualityRoute,
  path: "control-points",
  validateSearch: validateControlPointsSearch,
  component: ControlPointsScreen,
});
// `new` is declared BEFORE `$id/edit` and is a literal segment, so TanStack's
// specificity ranking cannot let a control point whose id is the string "new"
// shadow the create route.
const qualityControlPointNewRoute = createRoute({
  getParentRoute: () => qualityRoute,
  path: "control-points/new",
  validateSearch: validateControlPointNewSearch,
  component: () => <ControlPointEditorScreen mode="new" />,
});
const qualityControlPointEditRoute = createRoute({
  getParentRoute: () => qualityRoute,
  path: "control-points/$id/edit",
  component: () => <ControlPointEditorScreen mode="edit" />,
});
// §4.7 — the CoA manager (07-E): certificates + templates, two tabs on one
// route. The draft dialog and the gap panel live in component state — a
// half-composed draft is not an address anybody shares.
const qualityCoaRoute = createRoute({
  getParentRoute: () => qualityRoute,
  path: "coa",
  component: CoaManagerScreen,
});
// §4.8 — quality analytics (07-E): four panels on one route; every filter
// lives in component state (an analytics window is a glance, not an address —
// the §4.6 board's URL-held filters serve a different need).
const qualityAnalyticsRoute = createRoute({
  getParentRoute: () => qualityRoute,
  path: "analytics",
  component: QualityAnalyticsScreen,
});
// §4.1b — the failure-cause catalog (fix #117). `?mapping=unmapped` is held in
// the URL because "show me the causes that will refuse a scrap" is the question
// somebody arrives with from a 422 and pastes to a colleague.
const qualityFailureCausesRoute = createRoute({
  getParentRoute: () => qualityRoute,
  path: "failure-causes",
  validateSearch: validateFailureCausesSearch,
  component: FailureCausesScreen,
});

const qualityRouteTree = qualityRoute.addChildren([
  qualityIndexRoute,
  qualityInspectionsRoute,
  qualityInspectionRoute,
  qualityQuarantineRoute,
  qualityControlPointsRoute,
  qualityControlPointNewRoute,
  qualityControlPointEditRoute,
  qualityFailureCausesRoute,
  qualityCoaRoute,
  qualityAnalyticsRoute,
]);

// ---- Module 09 sales (slice P3-A part 2) -----------------------------------
//
// The foundations the rest of the module hangs off: §4.1 the customer book,
// §4.2 the 360 (header + Overview / Contacts & channels / Pricing tabs), and
// §4.3's three pricing-configuration screens — the price-list manager with its
// detail grid, the off-cut discount classes and the margin guards.
//
// WHAT GETS NO ROUTE, IN ANY FORM (D-033; spec 09 §4 slice ownership): the
// quote composer (§4.4), the quote list (§4.5), the review inbox (§4.6), the
// order list/detail (§4.7), the ship queue (§4.8), analytics (§4.9), the public
// pages (§4.10) and the triage inbox (§4.11). Their API paths are absent from
// openapi/v1.json entirely, so a 404 on a route that does not exist is the
// honest answer; a shell that renders "coming soon" is not. (Until P3-E PR-E2
// this said there was no sales-settings screen because §4 named none and the
// GET/PUT pair existed only for the reminder engine. The engine now reads it,
// and D-292 ruling 11 gives it a screen: `/sales/settings`, below.)
//
// The index is a COMPONENT redirect, not a `beforeLoad`: `beforeLoad` runs
// outside React and cannot read the role feed, so a static redirect would land
// a `sales.tier.manage`-only principal on the customer list they are refused
// (the module-05 defect, fixed there, not re-shipped here).
const salesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/sales",
  component: SalesLayout,
});
const salesIndexRoute = createRoute({
  getParentRoute: () => salesRoute,
  path: "/",
  component: SalesIndexRedirect,
});
// §4.1's whole state lives in the URL (PR-E4, D-305 rulings 3 and 17): the
// search box and the four filters as `q`, `tier`, `status`, `owner` and
// `credit_hold`, the wave-1b sort as `order` (D-242), and `view` naming a
// saved view. Until PR-E4 the filters were local state, on the argument that
// the 360 was the only address worth sharing; a saved view is a snapshot of
// URL state, so a filter kept anywhere else is one no view can store.
// Validated defensively: an unknown or malformed value is dropped, never
// thrown on, and a default is an absent parameter, so a bare
// `/sales/customers` is the address it always was
// (sales/screens/search-params.ts says why the strictness lives server-side).
const salesCustomersRoute = createRoute({
  getParentRoute: () => salesRoute,
  path: "customers",
  component: SalesCustomersScreen,
  validateSearch: validateSalesCustomersSearch,
});
const salesCustomer360Route = createRoute({
  getParentRoute: () => salesRoute,
  path: "customers/$customerId",
  component: SalesCustomer360Screen,
});
// §4.18 IMPORT CUSTOMERS (P3-E2 PR E2-2c, D-325 ruling 11): module 02's import
// flow on the `customers` kind, inside sales. The list-and-upload page and one
// job's page. `customers/import` is a static segment, so TanStack ranks it
// above `customers/$customerId` at the same depth whatever the declaration
// order, and no customer can be opened at the id "import"; the router unit
// test pins that rather than trusting the order here. The job's own page is
// `customers/import/$importId`, one segment deeper, so it never meets the 360.
const salesCustomersImportRoute = createRoute({
  getParentRoute: () => salesRoute,
  path: "customers/import",
  component: SalesCustomersImportScreen,
});
const salesCustomerImportJobRoute = createRoute({
  getParentRoute: () => salesRoute,
  path: "customers/import/$importId",
  component: SalesCustomerImportJobScreen,
});
// §4.17 MERGE (P3-E2 PR E2-2b, D-324 rulings 4 to 16): the customer in the
// address stays and the page picks the one merged into it. One segment deeper
// than the 360, so it never competes with `customers/$customerId`, and
// `customers/import/$importId` is a static first segment that outranks it for
// the word "import"; the router unit test pins both rather than trusting the
// declaration order.
const salesCustomerMergeRoute = createRoute({
  getParentRoute: () => salesRoute,
  path: "customers/$customerId/merge",
  component: SalesCustomerMergeScreen,
});
const salesPriceListsRoute = createRoute({
  getParentRoute: () => salesRoute,
  path: "price-lists",
  component: SalesPriceListsScreen,
});
const salesPriceListDetailRoute = createRoute({
  getParentRoute: () => salesRoute,
  path: "price-lists/$priceListId",
  component: SalesPriceListDetailScreen,
});
const salesOffcutClassesRoute = createRoute({
  getParentRoute: () => salesRoute,
  path: "offcut-classes",
  component: SalesOffcutClassesScreen,
});
const salesMarginGuardsRoute = createRoute({
  getParentRoute: () => salesRoute,
  path: "margin-guards",
  component: SalesMarginGuardsScreen,
});

// ---- Module 09 §4.4–§4.7 (slice P3-C-1) — the QUOTE CORE -------------------
//
// The quote book and the order list keep their whole state in the URL (PR-E4,
// D-305 rulings 3, 4 and 7). The quote book holds `customer`, `status`,
// `origin`, `owner`, `off_list`, `created_from`, `created_to`, the wave-1b
// sort `order` (D-242) and `view`; the order list holds `customer`, `status`,
// `facility`, `order` and `view`. `customer` was the first filter in the
// address because a LINK sets it (the customer 360's "all quotes for this
// customer", the duplicate-PO notice's link into the order list), and since
// PR-E4 the picker writes the same parameter, so the link and the picker set
// one state. Both routes validate with the router's one-argument call, which
// reads the D-301 verdict as `unknown` and keeps a money order; each screen
// validates again with the reader's verdict (sales/lib/value-order.ts).
//
// `quotes/new` is declared BEFORE `quotes/$quoteId` for readability only:
// TanStack ranks a static segment above a dynamic one at the same depth
// regardless of declaration order, so `/sales/quotes/new` can never resolve as
// a quote whose id is the word "new". The router unit test pins that rather
// than trusting the ordering here.
const salesQuotesRoute = createRoute({
  getParentRoute: () => salesRoute,
  path: "quotes",
  component: SalesQuotesScreen,
  validateSearch: validateSalesQuotesSearch,
});
// Both composer routes carry ONE optional param, `?customer_id=` — the
// document's customer, chosen by whoever linked here (the messaging inbox's
// "Create quote", spec 17 §4.1). It is declared on `orders/new` too because
// the same component serves both: a door into the order composer for a known
// customer costs nothing more than saying so.
const salesQuoteNewRoute = createRoute({
  getParentRoute: () => salesRoute,
  path: "quotes/new",
  component: SalesQuoteComposerScreen,
  validateSearch: validateSalesComposerSearch,
});
const salesQuoteDetailRoute = createRoute({
  getParentRoute: () => salesRoute,
  path: "quotes/$quoteId",
  component: SalesQuoteComposerScreen,
});
const salesReviewsRoute = createRoute({
  getParentRoute: () => salesRoute,
  path: "reviews",
  component: SalesReviewsScreen,
});
const salesOrdersRoute = createRoute({
  getParentRoute: () => salesRoute,
  path: "orders",
  component: SalesOrdersScreen,
  validateSearch: validateSalesOrdersSearch,
});
// `orders/new` is the §4.4 composer in ORDER MODE (P3-D PR-D1, D-283): the
// same component as `quotes/new` with `document="order"` — one grid, one
// price table, one verb that is `POST /v1/orders` rather than a draft save.
// Declared BEFORE `orders/$orderId` for readability only, exactly as
// `quotes/new` is: TanStack ranks a static segment above a dynamic one at the
// same depth regardless of declaration order, so `/sales/orders/new` can never
// resolve as an order whose id is the word "new". The router unit test
// (sales/__tests__/router-orders-new.test.tsx) pins that rather than trusting
// the ordering here.
const salesOrderNewRoute = createRoute({
  getParentRoute: () => salesRoute,
  path: "orders/new",
  component: () => <SalesQuoteComposerScreen document="order" />,
  validateSearch: validateSalesComposerSearch,
});
const salesOrderDetailRoute = createRoute({
  getParentRoute: () => salesRoute,
  path: "orders/$orderId",
  component: SalesOrderDetailScreen,
});
// §4.19 import orders (D-353 ruling 6). The static `orders/import` outranks
// `orders/$orderId` in any declaration order; router-orders-import.test.tsx pins it.
const salesOrdersImportRoute = createRoute({
  getParentRoute: () => salesRoute,
  path: "orders/import",
  component: SalesOrdersImportScreen,
});
const salesOrderImportJobRoute = createRoute({
  getParentRoute: () => salesRoute,
  path: "orders/import/$importId",
  component: SalesOrderImportJobScreen,
});

// ---- Module 09 §4.12–§4.13 (P3-E PR-E2, D-292 rulings 1, 11) ---------------
//
// The manager's TASK LIST and the SALES SETTINGS screen. The settings screen
// has no params.
//
// The task list keeps its whole state in the URL since P3-E2 PR E2-1 (spec 09
// §4.12, D-313, D-314): `scope`, `status`, `due`, `customer` and `assignee`,
// the order as `order`, `view` naming a saved view on `sales.tasks` (My day
// and Overdue are system views), and the side peek's `peek`
// (screens/tasks-search-params.ts gives the grammar). Until E2-1 it held
// `?customer=` alone and its Mine/All and Open/Done switches were component
// state, on the argument that they were the reader's own view of their list;
// a saved view is a snapshot of URL state, so a switch kept anywhere else is
// one no view can store. The `?customer=` a link sets lands as it always did.
const salesTasksRoute = createRoute({
  getParentRoute: () => salesRoute,
  path: "tasks",
  component: SalesTasksScreen,
  validateSearch: validateSalesTasksSearch,
});
const salesSettingsRoute = createRoute({
  getParentRoute: () => salesRoute,
  path: "settings",
  component: SalesSettingsScreen,
});
// §4.9 analytics (P3-E PR-E3, D-293). No params: the range and the tier filter
// are the reader's view of their own numbers, not an address worth sharing —
// the same call the task list's Mine/All switches make.
const salesAnalyticsRoute = createRoute({
  getParentRoute: () => salesRoute,
  path: "analytics",
  component: SalesAnalyticsScreen,
});

// ---- Module 09 §4.14 THE PIPELINE (P3-E2 PR E2-1, D-312, D-314) ------------
//
// Four addresses, one sub-nav entry (Pipeline, after Customers) and one tab
// strip the board, the list and the forecast share. Their audience is §7
// narrowing 15's five lineages (`canOpenPipeline`), the customer book's set;
// each screen states its own refusal, and the forecast is also a value door.
//
//   pipeline                       the board: `owner` and `customer` (shared
//                                  with the list under the list's keys),
//                                  `new_deal` and `from_conversation` (the
//                                  inbox's Create deal door) and `peek`. It
//                                  carries no saved view (D-312 ruling 20).
//   opportunities                  the deal list, on the `sales.opportunities`
//                                  saved-view grammar, plus `peek`.
//   opportunities/$opportunityId   the deal; no params.
//   forecast                       `from_month`, `to_month`, `owner`,
//                                  `customer`.
//
// Every validator is defensive (screens/pipeline-search-params.ts): a
// malformed value is dropped, never thrown on, and a default is an absent
// parameter. The list's validator runs here with the search alone, so a
// money order survives to the screen, which validates again with the reader's
// verdict (D-301), exactly as the quote and order books do.
//
// `opportunities` and `opportunities/$opportunityId` are siblings, as
// `quotes` and `quotes/$quoteId` are, so the deal page never renders inside
// the list; sales/__tests__/router-pipeline.test.tsx asks the real tree which
// route answers each address.
const salesPipelineRoute = createRoute({
  getParentRoute: () => salesRoute,
  path: "pipeline",
  component: SalesPipelineBoardScreen,
  validateSearch: validateSalesPipelineSearch,
});
const salesOpportunitiesRoute = createRoute({
  getParentRoute: () => salesRoute,
  path: "opportunities",
  component: SalesOpportunitiesScreen,
  validateSearch: validateSalesOpportunitiesSearch,
});
const salesOpportunityDetailRoute = createRoute({
  getParentRoute: () => salesRoute,
  path: "opportunities/$opportunityId",
  component: SalesOpportunityDetailScreen,
});
const salesForecastRoute = createRoute({
  getParentRoute: () => salesRoute,
  path: "forecast",
  component: SalesForecastScreen,
  validateSearch: validateSalesForecastSearch,
});

const salesRouteTree = salesRoute.addChildren([
  salesIndexRoute,
  salesCustomersRoute,
  salesCustomersImportRoute,
  salesCustomerImportJobRoute,
  salesCustomer360Route,
  salesCustomerMergeRoute,
  salesPipelineRoute,
  salesOpportunitiesRoute,
  salesOpportunityDetailRoute,
  salesForecastRoute,
  salesQuotesRoute,
  salesQuoteNewRoute,
  salesQuoteDetailRoute,
  salesReviewsRoute,
  salesOrdersRoute,
  salesOrderNewRoute,
  salesOrdersImportRoute,
  salesOrderImportJobRoute,
  salesOrderDetailRoute,
  salesTasksRoute,
  salesAnalyticsRoute,
  salesPriceListsRoute,
  salesPriceListDetailRoute,
  salesOffcutClassesRoute,
  salesMarginGuardsRoute,
  salesSettingsRoute,
]);

// ---- Module 17 messaging (slice P3-B part 2) -------------------------------
//
// The office half of module 17: §4.1's conversations inbox (hosting §4.2's
// unclaimed work as its `unclaimed` tab — §4.2's wording admits either form,
// and one screen means one thread renderer to keep correct), §4.3's template
// catalog and §4.4's ga/fa-only settings screen. The §4.5 link widget is
// EMBEDDED in the sales customer 360 (components/link-widget.tsx), not routed.
//
// WHAT GETS NO ROUTE, IN ANY FORM (D-033): no §4.6 platform-console screen
// here (admin area, platform principals, its own slice) and no own-profile
// §4.5 surface — the client has no `/v1/auth/me` to learn its own user id
// from (lib/role-codes.ts: memberships, not a subject), so that leg is a
// stated absence in the link widget's header rather than a screen that
// cannot fill its one request field.
//
// The index is a COMPONENT redirect (the sales/waste/quality precedent):
// `beforeLoad` cannot read the role feed, and a static redirect would land a
// qc lead — who holds `conversation.read` and nothing else — on a screen they
// are refused. §4.1's tab, search and selected thread live in the URL (the
// module-04 work-order-list rule: a shared worklist for ~10 managers must
// paste as an address and survive a reload).
const messagingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/messaging",
  component: MessagingLayout,
});
const messagingIndexRoute = createRoute({
  getParentRoute: () => messagingRoute,
  path: "/",
  component: MessagingIndexRedirect,
});
const messagingInboxRoute = createRoute({
  getParentRoute: () => messagingRoute,
  path: "inbox",
  validateSearch: validateInboxSearch,
  component: MessagingInboxScreen,
});
const messagingTemplatesRoute = createRoute({
  getParentRoute: () => messagingRoute,
  path: "templates",
  component: MessagingTemplatesScreen,
});
const messagingSettingsRoute = createRoute({
  getParentRoute: () => messagingRoute,
  path: "settings",
  component: MessagingSettingsScreen,
});

const messagingRouteTree = messagingRoute.addChildren([
  messagingIndexRoute,
  messagingInboxRoute,
  messagingTemplatesRoute,
  messagingSettingsRoute,
]);

// ---- Module 03 planning (spec 03 §4.1 to §4.8, §4.12, §4.13; D-354) --------
//
// `/planning` is the workbench, which every `planning.read` holder reads. The
// calendars screen reads on `planning.calendar.read` (P3-H-1, D-390), and the
// planned orders and the run detail on `planning.planned_order.read` (P3-H-2);
// the layout refuses a screen whose code the reader lacks. The planned-orders
// list keeps its filters, its tab and the open drawer (`order`) in the URL.
const planningRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/planning",
  component: PlanningLayout,
});
const planningIndexRoute = createRoute({
  getParentRoute: () => planningRoute,
  path: "/",
  component: PlanningWorkbenchScreen,
});
const planningDemandRoute = createRoute({
  getParentRoute: () => planningRoute,
  path: "demand",
  component: PlanningDemandScreen,
});
const planningForecastsRoute = createRoute({
  getParentRoute: () => planningRoute,
  path: "forecasts",
  component: PlanningForecastsScreen,
});
const planningForecastRoute = createRoute({
  getParentRoute: () => planningRoute,
  path: "forecasts/$forecastId",
  component: PlanningForecastEditorScreen,
});
const planningMpsRoute = createRoute({
  getParentRoute: () => planningRoute,
  path: "mps",
  component: PlanningMpsScreen,
});
const planningOrdersRoute = createRoute({
  getParentRoute: () => planningRoute,
  path: "orders",
  validateSearch: validatePlannedOrdersSearch,
  component: PlanningPlannedOrdersScreen,
});
const planningMrpRunRoute = createRoute({
  getParentRoute: () => planningRoute,
  path: "mrp/runs/$runId",
  component: PlanningMrpRunScreen,
});
// `/planning/mrp` has no screen of its own: the run detail sits under it and
// the runs are listed on the workbench, so a truncated run address or an old
// bookmark lands on the planned orders, which read on the same code
// (`PLANNING_UNLISTED` in hooks/permissions.ts). It used to answer TanStack's
// bare "Not Found" inside the planning shell.
const planningMrpIndexRoute = createRoute({
  getParentRoute: () => planningRoute,
  path: "mrp",
  beforeLoad: () => {
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw redirect({ to: "/planning/orders" });
  },
});
const planningSettingsRoute = createRoute({
  getParentRoute: () => planningRoute,
  path: "settings",
  component: PlanningSettingsScreen,
});
const planningParametersRoute = createRoute({
  getParentRoute: () => planningRoute,
  path: "settings/parameters",
  component: PlanningParametersScreen,
});
const planningCalendarsRoute = createRoute({
  getParentRoute: () => planningRoute,
  path: "settings/calendars",
  component: PlanningCalendarsScreen,
});
// P3-I-2 (§4.6 to §4.8, D-406 ruling 3): the schedule board and the capacity
// view read on `planning.schedule.read`, the setup matrix on
// `planning.calendar.read`. Each keeps its window, zoom, lane and filters, or
// its chosen work centre, in the URL, so a capacity row links to the board on
// its lane and week.
const planningScheduleRoute = createRoute({
  getParentRoute: () => planningRoute,
  path: "schedule",
  validateSearch: validateScheduleSearch,
  component: PlanningScheduleScreen,
});
const planningCapacityRoute = createRoute({
  getParentRoute: () => planningRoute,
  path: "capacity",
  validateSearch: validateCapacitySearch,
  component: PlanningCapacityScreen,
});
const planningSetupMatrixRoute = createRoute({
  getParentRoute: () => planningRoute,
  path: "settings/setup-matrix",
  validateSearch: validateSetupMatrixSearch,
  component: PlanningSetupMatrixScreen,
});
// P3-J-1 (§4.9, D-474 ruling 1): the promises, read on `planning.read`. The
// list keeps its filters, the open drawer (`promise`) and the CTP dialog
// (`check`, bound to an order line by `line`) in the URL.
const planningPromisesRoute = createRoute({
  getParentRoute: () => planningRoute,
  path: "promises",
  validateSearch: validatePromisesSearch,
  component: PlanningPromisesScreen,
});
// P3-J J-4 (§4.10, D-474 ruling 1): the what-if sandbox, in the Schedule
// section, read and written on `planning.scenario.manage`. The list keeps its
// status filter in the URL, the builder has none, and the result keeps the
// first date its sandbox lanes show (`from`).
const planningScenariosRoute = createRoute({
  getParentRoute: () => planningRoute,
  path: "scenarios",
  validateSearch: validateScenariosSearch,
  component: PlanningScenariosScreen,
});
const planningScenarioNewRoute = createRoute({
  getParentRoute: () => planningRoute,
  path: "scenarios/new",
  component: PlanningScenarioBuilderScreen,
});
const planningScenarioRoute = createRoute({
  getParentRoute: () => planningRoute,
  path: "scenarios/$scenarioId",
  validateSearch: validateScenarioResultSearch,
  component: PlanningScenarioResultScreen,
});
const planningRouteTree = planningRoute.addChildren([
  planningIndexRoute,
  planningDemandRoute,
  planningForecastsRoute,
  planningForecastRoute,
  planningMpsRoute,
  planningOrdersRoute,
  planningMrpIndexRoute,
  planningMrpRunRoute,
  planningScheduleRoute,
  planningCapacityRoute,
  planningScenariosRoute,
  planningScenarioNewRoute,
  planningScenarioRoute,
  planningPromisesRoute,
  planningSettingsRoute,
  planningParametersRoute,
  planningCalendarsRoute,
  planningSetupMatrixRoute,
]);

// ---- Module 14 agents (spec 14 §4.2 to §4.4, D-512, D-529) -----------------
//
// The Hub, the journal and an agent's detail. The Hub keeps its tab and the
// guardrail filters in the URL, and the journal its tab, filters, sort and the
// one card a notification opens (`action`). `journal` is a static segment, so
// it wins over `$agentId`.
const agentsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/agents",
  component: AgentsLayout,
});
function AgentsHubRouteScreen() {
  const search = agentsHubRoute.useSearch();
  const navigate = agentsHubRoute.useNavigate();
  return (
    <AgentsHubScreen
      search={search}
      onSearch={(next) => {
        void navigate({ search: next, replace: true });
      }}
    />
  );
}
const agentsHubRoute = createRoute({
  getParentRoute: () => agentsRoute,
  path: "/",
  validateSearch: validateHubSearch,
  component: AgentsHubRouteScreen,
});
function AgentsJournalRouteScreen() {
  const search = agentsJournalRoute.useSearch();
  const navigate = agentsJournalRoute.useNavigate();
  return (
    <AgentsJournalScreen
      search={search}
      onSearch={(next) => {
        void navigate({ search: next, replace: true });
      }}
    />
  );
}
const agentsJournalRoute = createRoute({
  getParentRoute: () => agentsRoute,
  path: "journal",
  validateSearch: validateJournalSearch,
  component: AgentsJournalRouteScreen,
});
function AgentDetailRouteScreen() {
  const { agentId } = agentDetailRoute.useParams();
  return <AgentDetailScreen key={agentId} agentId={agentId} />;
}
const agentDetailRoute = createRoute({
  getParentRoute: () => agentsRoute,
  path: "$agentId",
  component: AgentDetailRouteScreen,
});
const agentsRouteTree = agentsRoute.addChildren([
  agentsHubRoute,
  agentsJournalRoute,
  agentDetailRoute,
]);

// ---- The audit trail (spec 16 §2.16/§4) ------------------------------------
//
// ONE route, no area shell and no index redirect: the area has exactly one
// screen and the same screen answers both of its questions. The entity-scoped
// view — "everything that happened to THIS record" — is this route with
// `?entity_type=&entity_id=`, which is why the filters are URL-held: a deep
// link IS the History affordance an entity screen hands out, and it has to
// survive a reload, a share, and a paste into a dispute email.
const auditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/audit",
  validateSearch: validateAuditSearch,
  component: AuditTrailScreen,
});

// S9 Terminology settings sits OUTSIDE the wizard shell: it is owned by module
// 02 but lives in settings and stays available after go-live (§4 S9).
const terminologySettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/terminology",
  component: TerminologySettingsScreen,
});

// Settings · Industry pack (spec 02 §4 S13, D-343): the installed pack and the
// previewed update. Outside the wizard shell for the same reason as S9: the
// tenant who needs it is usually live, and the wizard is history by then.
const industryPackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/industry-pack",
  component: IndustryPackScreen,
});

// ---- Floor PWA (Module 01 S9 + Module 06 §4 floor rows, D-048) -------------
//
// `/floor` is the shell: it mounts the device outbox (so the queue drains
// whenever the app is open, on any screen) and renders the ONE connectivity +
// pending-count banner every floor screen is required to carry.
//
// The module-06 slice: `/floor` hub → scan (§3.5) · stock (§4.1 floor) · unit
// card with the guided cut (§4.2 floor / §3.2) · transfer receive (§4.4 floor)
// · blind count worksheet (§4.5 floor) · the needs-attention tray (§9.2). The
// Module 01 product-card lookup keeps its own `/floor/lookup` path — it answers
// a different question ("what IS this product") than `/floor/stock` ("where is
// the stock"), and collapsing them would lose one of them.

const floorRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/floor",
  component: FloorLayout,
});
const floorIndexRoute = createRoute({
  getParentRoute: () => floorRoute,
  path: "/",
  component: FloorHomeScreen,
});
const floorLookupRoute = createRoute({
  getParentRoute: () => floorRoute,
  path: "lookup",
  component: FloorLookupScreen,
});
const floorScanRoute = createRoute({
  getParentRoute: () => floorRoute,
  path: "scan",
  component: FloorScanScreen,
});
const floorStockRoute = createRoute({
  getParentRoute: () => floorRoute,
  path: "stock",
  component: FloorStockScreen,
});
const floorUnitRoute = createRoute({
  getParentRoute: () => floorRoute,
  path: "units/$id",
  component: FloorUnitScreen,
});
const floorCountsRoute = createRoute({
  getParentRoute: () => floorRoute,
  path: "counts",
  component: FloorCountsScreen,
});
const floorCountRoute = createRoute({
  getParentRoute: () => floorRoute,
  path: "counts/$id",
  component: FloorCountWorksheetScreen,
});
const floorReceiveListRoute = createRoute({
  getParentRoute: () => floorRoute,
  path: "receive",
  component: FloorReceiveListScreen,
});
const floorReceiveRoute = createRoute({
  getParentRoute: () => floorRoute,
  path: "receive/$id",
  component: FloorReceiveScreen,
});
const floorTrayRoute = createRoute({
  getParentRoute: () => floorRoute,
  path: "tray",
  component: FloorTrayScreen,
});

// ---- Module 09 floor slice (P3-D PR-D3, D-286): §4.8 ship queue + pick ----
// `/floor/ship` is the queue (orders shippable at this facility, one tap per
// order); `/floor/ship/$orderId` is the pick screen, addressed by ORDER because
// that is what the storekeeper holds — the shipment document behind it is
// opened by the screen itself. The receive list/manifest pair is the shape.
// Both ride the ONE `assessly.floor` outbox (the picks queue; the open is
// online-only — floor-ship.tsx's header says why).
const floorShipQueueRoute = createRoute({
  getParentRoute: () => floorRoute,
  path: "ship",
  component: FloorShipQueueScreen,
});
const floorShipRoute = createRoute({
  getParentRoute: () => floorRoute,
  path: "ship/$orderId",
  component: FloorShipScreen,
});

// ---- Module 04 floor slice (04-B-3): S9 station home + S10 op run ----------
// `/floor/station` is the picker (station identity WITHOUT a QR in hand);
// `/floor/station/$workCenterId` is S9; the op run nests under its station
// because the station QR is the context anchor (§3.3 step 2) and an op is
// only ever reached through a station's queue.
const floorStationPickerRoute = createRoute({
  getParentRoute: () => floorRoute,
  path: "station",
  component: StationPickerScreen,
});
const floorStationRoute = createRoute({
  getParentRoute: () => floorRoute,
  path: "station/$workCenterId",
  component: StationHomeScreen,
});
const floorOpRunRoute = createRoute({
  getParentRoute: () => floorRoute,
  path: "station/$workCenterId/op/$opId",
  component: OpRunScreen,
});

// ---- Module 05 floor slice (05-B): S8 waste capture + S9 remnant intake ----
// Both nest under the station because the station QR is the context anchor
// (spec 05 §4 S8 "floor PWA, station context") — the capture pre-fills the
// work centre and the shift attribution rides the server-resolved occurred_at
// (D-102), never a client claim. Offline-first: both screens enqueue into the
// same `assessly.floor` outbox the production taps drain (§9.1/D-080).
const floorWasteCaptureRoute = createRoute({
  getParentRoute: () => floorRoute,
  path: "station/$workCenterId/waste",
  component: FloorWasteCaptureScreen,
});
const floorRemnantRoute = createRoute({
  getParentRoute: () => floorRoute,
  path: "station/$workCenterId/remnant",
  component: FloorRemnantScreen,
});

// ---- Module 07 floor slice (07-A): S4.3 floor queue + S4.4 execution -------
// NOT nested under a station: an inspection's context anchor is the INSPECTION
// (its subject is a bobbin, a lot or an operation — §2.5), not the machine the
// tablet is pinned to, and a receiving check has no station at all. Both
// screens ride the ONE `assessly.floor` outbox; the execution screen is the
// same component the office mounts at /quality/inspections/$id.
const floorQualityQueueRoute = createRoute({
  getParentRoute: () => floorRoute,
  path: "quality",
  component: FloorQualityQueueScreen,
});
// §4.8's floor version (review F16): the read-only shift scoreboard for the
// worker's own facility. A LITERAL segment declared before the $id run route,
// so an inspection whose id is the string "scoreboard" can never shadow it
// (the control-points/new precedent).
const floorQualityScoreboardRoute = createRoute({
  getParentRoute: () => floorRoute,
  path: "quality/scoreboard",
  component: FloorScoreboardScreen,
});
const floorQualityRunRoute = createRoute({
  getParentRoute: () => floorRoute,
  path: "quality/$id",
  component: FloorQualityRunScreen,
});

// ---- Module 07 floor slice (07-C-3): S5, the guided cut --------------------
// `/floor/quality/$id/cut` — the id is the INSPECTION's, not the cut event's,
// and that is D-162's discovery mechanism made into a URL: §5 registers NO list
// route for cut events on purpose, because the inspection is the thing the
// operator has in their hand and `guided_cut.cut_event_id` on its read is how
// the live cut is found. A `/floor/cut/$cutEventId` route would be addressable
// only by somebody who already had an id no screen shows them.
//
// ONE screen, on the FLOOR, and no office twin (§4.5's own heading: "Guided Cut
// screen (floor PWA)"). §4.4 says "office fallback identical" and §4.5
// deliberately does not — a cut is taken at the saw. The §4.4 sheet's fifth
// control therefore LINKS here from both shells rather than rendering a second
// copy of the cut lifecycle under the office chrome, which would be a screen the
// spec does not name and a second place to keep propose/confirm/execute correct.
const floorQualityCutRoute = createRoute({
  getParentRoute: () => floorRoute,
  path: "quality/$id/cut",
  component: FloorGuidedCutScreen,
});

// ---- Module 04 floor slice (04-C-4): S11 my-shift + S12 wall mode ---------
// `/floor/my-shift` is a WORKER's own tally — no id in the path, because e16
// aggregates the SESSION principal and there is nothing to address.
// `/wall` (spec §4 S12 — the path really is `/wall`, not `/floor/wall`) is
// FURNITURE: a tablet taped to a wall, signed in as an ordinary floor session
// (D-087 ruling 1 — no scoped wall token exists), with no controls of its own.
// It sits outside the FLOOR shell — back link, nav, sync banner, none of which
// belong on a board nobody touches — and outside the OFFICE shell too, which is
// what `rootRoute` renders and which was strictly worse: it put five nav links
// and `Sign out` on a corridor tablet. `RootLayout` branches to a chrome-less
// `WallShell` for this path (routes/root.tsx); the zero-control property is
// asserted against that shell, not against the screen alone.
const floorMyShiftRoute = createRoute({
  getParentRoute: () => floorRoute,
  path: "my-shift",
  component: MyShiftScreen,
});
const wallBoardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/wall",
  component: WallBoardScreen,
});

const floorRouteTree = floorRoute.addChildren([
  floorIndexRoute,
  floorLookupRoute,
  floorScanRoute,
  floorStockRoute,
  floorUnitRoute,
  floorCountsRoute,
  floorCountRoute,
  floorReceiveListRoute,
  floorReceiveRoute,
  floorShipQueueRoute,
  floorShipRoute,
  floorTrayRoute,
  floorStationPickerRoute,
  floorStationRoute,
  floorOpRunRoute,
  floorWasteCaptureRoute,
  floorRemnantRoute,
  floorQualityQueueRoute,
  floorQualityScoreboardRoute,
  floorQualityRunRoute,
  floorQualityCutRoute,
  floorMyShiftRoute,
]);

// ---- Canonical scan paths (module 02 QR kit · spec 04 §3.3/§5 e29) ---------
// A camera scan opens the printed URL directly, so these are REAL routes, not
// only parser grammar. SEAM: scan.ts's wc/op recognisers (this same slice's
// outbox sub-step) feed the identical paths from keyboard-wedge input.
//   /q/wc/{uuid} — pin the station, land on S9. Pinning IS the redirect's
//     side effect: the station QR is the context anchor.
//   /q/op/{uuid} — the traveler's per-op shortcut; resolved against the
//     pinned station's snapshot (screens/qr-resolve.tsx).
const qrStationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/q/wc/$workCenterId",
  beforeLoad: ({ params }) => {
    pinStation({ workCenterId: params.workCenterId });
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw redirect({
      to: "/floor/station/$workCenterId",
      params: { workCenterId: params.workCenterId },
    });
  },
});
const qrOpRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/q/op/$opId",
  component: QrOpResolverScreen,
});

// ---- Module 09 §4.10 — THE PUBLIC DOORS (slice P3-C-1) ---------------------
//
// `/q/$token` (the tokenized quote) and `/track/$publicToken` (order
// tracking). Both are UNAUTHENTICATED BY CONSTRUCTION — the token IS the
// credential, exactly as `/accept-invite` is — and both render outside the
// office AppShell, outside the floor shell, and outside every session guard
// (routes/root.tsx branches to the public shell on these paths).
//
// THE `/q` COLLISION, and why it is pinned rather than assumed. `/q/wc/$id`
// and `/q/op/$id` above are the floor's QR resolvers, and a ONE-segment
// `/q/$token` is exactly the shape that would swallow a mistyped scan path.
// It does not: TanStack matches on segment COUNT first (two segments vs one),
// and ranks a static segment above a dynamic one within the same count, so
// `/q/wc/abc` can only ever reach the work-centre resolver. That is a property
// of the router's ranking, not of this file's ordering — which is precisely
// why it is asserted in routes/__tests__ rather than trusted to a comment.
// Get it wrong and a floor operator scanning a smudged QR lands on a customer
// quote page, which is both a broken scan and a data leak.
const publicQuoteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/q/$token",
  component: PublicQuoteScreen,
});

const publicTrackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/track/$publicToken",
  component: PublicOrderTrackScreen,
});

// ---- Platform console (spec 15 §4) — admin.<apex> ONLY ---------------------
// The console's routes are its own table: areas/admin/console-routes.tsx,
// whose header is the contract the M15 lanes add their screens by.

// Spec 15 §4 S19 (M15-8, D-620): the tenant-host page the console's one-time
// support link lands on. It redeems the code and goes into the app, where the
// banner says who is using the account.
const impersonateRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/impersonate",
  validateSearch: (search: Record<string, unknown>): { code?: string; ended?: string } => ({
    ...(typeof search["code"] === "string" ? { code: search["code"] } : {}),
    ...(search["ended"] === "1" || search["ended"] === 1 ? { ended: "1" } : {}),
  }),
  component: ImpersonationLanding,
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  forgotPasswordRoute,
  resetPasswordRoute,
  ...consoleRouteTrees,
  impersonateRoute,
  homeRoute,
  productsRedirectRoute,
  newProductRedirectRoute,
  catalogueRouteTree,
  inventoryRouteTree,
  wasteRouteTree,
  productionRouteTree,
  qualityRouteTree,
  salesRouteTree,
  messagingRouteTree,
  planningRouteTree,
  agentsRouteTree,
  onboardingRouteTree,
  auditRoute,
  terminologySettingsRoute,
  industryPackRoute,
  floorRouteTree,
  wallBoardRoute,
  qrStationRoute,
  qrOpRoute,
  publicQuoteRoute,
  publicTrackRoute,
]);

// A path no child of an area's shell matches renders inside that shell, and
// the root's `notFoundComponent` does not reach it: TanStack falls back to the
// router's default, the bare `<p>Not Found</p>` (census of the demo tenant,
// 2026-10-16, at `/planning/mrp`). Every shell now shows the designed page.
export const router = createRouter({ routeTree, defaultNotFoundComponent: NotFoundPage });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
